import React, { useState } from 'react';

import {
  Button,
  Collapsible,
  CollapsibleContent,
  Icon,
  Stack,
  Text,
} from '@eluve/components';
import { formatToLocale, timestampToHumanReadable } from '@eluve/date-utils';
import { useHumanFriendlyError } from '@eluve/frontend-human-friendly-errors';
import { VendorTypesEnum, isVendorWebBased } from '@eluve/graphql-types';
import { getVendorDataMapper } from '@eluve/vendor';

type EhrOverviewProps = {
  vendor: VendorTypesEnum;
  domain: string;
  logo: (c: boolean) => React.JSX.Element;
  status?: 'CONNECTED' | 'DISCONNECTED';
  error?: string | null;
  syncedAt?: number;
  showStatusDetails?: boolean;
};

type EhrStatusDetailsProps = {
  vendor: VendorTypesEnum;
  domain: string;
  status?: 'CONNECTED' | 'DISCONNECTED';
  error?: string | null;
  syncedAt?: number;
};

export const EhrStatusDetails: React.FC<EhrStatusDetailsProps> = ({
  vendor,
  domain,
  status,
  error,
  syncedAt,
}) => {
  const toHumanFriendly = useHumanFriendlyError();
  const vendorProvider = getVendorDataMapper(vendor);

  const getEhrStatusEntry = (key: string, value: string | undefined) => {
    if (!value) {
      return null;
    }
    return (
      <Stack
        className="my-3"
        direction="horizontal"
        align="center"
        justify="between"
      >
        <Text color="disabled" className="md:text-xs">
          {key}
        </Text>
        <Text color="tertiary" className="md:text-xs">
          {value}
        </Text>
      </Stack>
    );
  };
  const errorDisplay = (errorMessage?: string) => (
    <Stack
      className="self-center bg-bg-warning-subtle p-1"
      direction="horizontal"
      align="stretch"
    >
      <Text className="font-light md:text-xs">
        {toHumanFriendly(errorMessage ?? '').message}
      </Text>
    </Stack>
  );

  return (
    <div className="my-3 w-full" key={`current-account-overview-${vendor}`}>
      {status === 'DISCONNECTED' && errorDisplay(error ?? '')}
      {getEhrStatusEntry('EHR', vendor)}
      {getEhrStatusEntry('Status', status)}
      {getEhrStatusEntry(
        'Last Synced',
        syncedAt ? formatToLocale(new Date(syncedAt).toString()) : '',
      )}
      <Stack justify="between" align="stretch">
        <Button
          variant="ghost"
          className="mt-2 justify-start border fill-transparent px-0 hover:bg-transparent"
          onClick={() => {
            window.open(
              vendorProvider.getHomeUrl(domain),
              '_blank',
              'noopener,noreferrer',
            );
          }}
        >
          <Stack direction="horizontal" align="center">
            <Icon name="ExternalLink" size="lg" className="m-2" />
            <Text inline size="xs">
              {domain}
            </Text>
          </Stack>
        </Button>
      </Stack>
    </div>
  );
};

export const EhrOverview: React.FC<EhrOverviewProps> = ({
  vendor,
  domain,
  logo,
  status,
  error,
  syncedAt,
  showStatusDetails = false,
}) => {
  let subTitle = '';
  if (status === 'DISCONNECTED') {
    subTitle = 'Sync Failed';
  } else {
    if (syncedAt) {
      subTitle = `Synced ${timestampToHumanReadable(syncedAt)}`;
    }
  }

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const webBasedVendor = isVendorWebBased(vendor);

  const isConnected = webBasedVendor ? status === 'CONNECTED' : true;

  return (
    <Stack
      key={`account-status-${vendor}`}
      direction="horizontal"
      align="stretch"
    >
      <Stack align="stretch">
        <Stack direction="horizontal" justify="between" align="stretch">
          <Stack
            className="h-10 self-center"
            direction="horizontal"
            align="stretch"
          >
            {logo(isConnected)}
            <div className="ml-3">
              <Text>{vendor}</Text>
              <Stack direction="horizontal" align="stretch">
                <div className="self-center">
                  {isConnected && (
                    <span className="mr-2 inline-block size-2 rounded-full bg-[#46A758]" />
                  )}
                  {status === 'DISCONNECTED' && (
                    <span className="mr-2 inline-block size-2 rounded-full bg-[#F09234]" />
                  )}
                </div>
                <Text inline color="disabled" className="md:text-xs">
                  {subTitle}
                </Text>
              </Stack>
            </div>
          </Stack>
          {showStatusDetails && (
            <div className="self-center">
              {isOpen ? (
                <div
                  className="cursor-pointer p-1"
                  onClick={() => {
                    setIsOpen(false);
                  }}
                >
                  <Icon name="ChevronUp" />
                </div>
              ) : (
                <div
                  className="cursor-pointer p-1"
                  onClick={() => {
                    setIsOpen(true);
                  }}
                >
                  <Icon name="ChevronDown" />
                </div>
              )}
            </div>
          )}
        </Stack>
        <Collapsible open={isOpen}>
          <CollapsibleContent>
            <EhrStatusDetails
              vendor={vendor}
              domain={domain}
              error={error}
              status={status}
              syncedAt={syncedAt}
            />
          </CollapsibleContent>
        </Collapsible>
      </Stack>
    </Stack>
  );
};
